import { PrototypeApiError } from './apiTransport';

export interface MediaChapterTransitionCommit {
	committed: boolean;
	previousLeaseId?: string;
}

export interface MediaChapterTransitionFailure {
	error: unknown;
	chapter: number;
}

export interface MediaChapterTransitionCommitContext {
	signal: AbortSignal;
}

export interface MediaChapterTransitionOptions<Response> {
	initialChapter: number;
	getTotalChapters(): number | Promise<number>;
	request(chapter: number): Promise<Response>;
	readLeaseId(response: Response): string | undefined;
	release(playbackLeaseId: string): Promise<unknown> | unknown;
	releaseRetryWait?(delayMs: number): Promise<void>;
	commit(
		chapter: number,
		response: Response,
		context: MediaChapterTransitionCommitContext,
	): MediaChapterTransitionCommit | Promise<MediaChapterTransitionCommit>;
	onError(message: string, failure: MediaChapterTransitionFailure): void;
	/**
	 * Optional client-side access pre-check. Called before `request` (the
	 * token fetch). Return true to block the transition -- the coordinator
	 * will NOT send the token request and will instead call `onError` with
	 * a paywall/login message. This prevents the server from returning a
	 * 403/401 that the error handler would mislabel as "couldn't load".
	 */
	shouldBlockChapter?(chapter: number): boolean;
	/** Fired when a chapter request starts (token fetch + video load). */
	onTransitionStart?(chapter: number): void;
	/** Fired when a chapter transition settles (committed, cancelled, or failed). */
	onTransitionEnd?(chapter: number): void;
}

export interface MediaChapterTransitionCoordinator {
	move(direction: 1 | -1): void;
	goTo(chapter: number): void;
	syncMounted(chapter: number): void;
	retry(): void;
	cleanup(): void;
	snapshot(): {
		mountedChapter: number;
		desiredChapter: number;
		requestedChapter: number | null;
		retryPending: boolean;
		retryNotBefore: number;
		pendingLeaseIds: string[];
		disposed: boolean;
	};
}

export function createMediaChapterTransitionCoordinator<Response>(
	options: MediaChapterTransitionOptions<Response>,
): MediaChapterTransitionCoordinator {
	let mountedChapter = normalizeChapter(options.initialChapter);
	let desiredChapter = mountedChapter;
	let requestedChapter: number | null = null;
	let retryPending = false;
	let retryNotBefore = 0;
	let totalChapters: number | null = null;
	let totalResolution: Promise<void> | null = null;
	let commitAbortController: AbortController | null = null;
	let disposed = false;
	const pendingLeaseIds = new Set<string>();
	const releaseInFlight = new Map<string, Promise<boolean>>();
	const cleanupRetriedLeaseIds = new Set<string>();
	// The lease ID of the most recently mounted chapter. The next pump() pass
	// releases this BEFORE request() so the server never sees the previous
	// chapter's lease count toward the cap during the new reservation. Without
	// this, rapid swipes (three chapters in two seconds) can hit
	// `playback_limit_reached` because leases accumulate to the cap before the
	// previous chapter's release HTTP round-trip completes.
	let mountedLeaseId: string | undefined;
	const releaseRetryWait = options.releaseRetryWait ?? ((delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs)));

	const releaseLease = (leaseId: string): Promise<boolean> => {
		const existing = releaseInFlight.get(leaseId);
		if (existing) return existing;
		pendingLeaseIds.add(leaseId);
		const operation = (async () => {
			for (let attempt = 0; attempt < 3; attempt += 1) {
				try {
					await options.release(leaseId);
					pendingLeaseIds.delete(leaseId);
					return true;
				} catch (error) {
					if (error instanceof PrototypeApiError && error.status === 404) {
						pendingLeaseIds.delete(leaseId);
						return true;
					}
					if (attempt < 2) await releaseRetryWait(attempt === 0 ? 50 : 150);
				}
			}
			return false;
		})().finally(() => releaseInFlight.delete(leaseId));
		releaseInFlight.set(leaseId, operation);
		return operation;
	};

	const retryPendingLeaseOnCleanup = (leaseId: string): void => {
		if (cleanupRetriedLeaseIds.has(leaseId)) return;
		cleanupRetriedLeaseIds.add(leaseId);
		void (async () => {
			const inFlight = releaseInFlight.get(leaseId);
			if (inFlight) await inFlight;
			if (pendingLeaseIds.has(leaseId)) await releaseLease(leaseId);
		})();
	};

	const safeReleaseResponse = async (response: Response): Promise<void> => {
		const leaseId = options.readLeaseId(response);
		if (!leaseId) return;
		const released = await releaseLease(leaseId);
		if (!released && disposed) retryPendingLeaseOnCleanup(leaseId);
	};

	const failTotalResolution = (error: unknown): void => {
		if (disposed) return;
		retryPending = true;
		retryNotBefore = 0;
		options.onError('Video could not load. Tap to retry.', { error, chapter: desiredChapter });
	};

	function clampToKnownTotal(chapter: number): number {
		return totalChapters === null ? chapter : Math.min(totalChapters, chapter);
	}

	function ensureTotalChapters(): boolean {
		if (totalChapters !== null) {
			desiredChapter = clampToKnownTotal(desiredChapter);
			return true;
		}
		if (totalResolution) return false;
		let result: number | Promise<number>;
		try {
			result = options.getTotalChapters();
		} catch (error) {
			failTotalResolution(error);
			return false;
		}
		if (typeof result === 'number') {
			if (!Number.isFinite(result) || result <= 0) {
				failTotalResolution(new Error('chapter_count_unavailable'));
				return false;
			}
			totalChapters = Math.max(1, Math.floor(result));
			desiredChapter = clampToKnownTotal(desiredChapter);
			return true;
		}
		totalResolution = Promise.resolve(result)
			.then((total) => {
				if (disposed) return;
				if (!Number.isFinite(total) || total <= 0) {
					failTotalResolution(new Error('chapter_count_unavailable'));
					return;
				}
				totalChapters = Math.max(1, Math.floor(total));
				desiredChapter = clampToKnownTotal(desiredChapter);
			})
			.catch((error) => failTotalResolution(error))
			.finally(() => {
				totalResolution = null;
				if (!disposed && totalChapters !== null && !retryPending) void pump();
			});
		return false;
	}

	const pump = async (): Promise<void> => {
		if (disposed || requestedChapter !== null || retryPending || desiredChapter === mountedChapter) return;
		if (Date.now() < retryNotBefore) {
			retryPending = true;
			return;
		}
		if (!ensureTotalChapters() || desiredChapter === mountedChapter) return;
		const targetChapter = desiredChapter;
		// Release the previous chapter's lease BEFORE the new reservation so the
		// server's lease cap check sees the slot freed. The release is awaited
		// (best-effort, with a short timeout) so that the cap check on the next
		// request does not observe the previous lease. The release uses
		// keepalive/postBackground on the transport layer; if the request fails
		// or times out, the previous lease will expire server-side after its
		// TTL anyway, so we never permanently strand it.
		if (mountedLeaseId) {
			const previousLeaseId = mountedLeaseId;
			mountedLeaseId = undefined;
			try {
				await Promise.race([
					releaseLease(previousLeaseId),
					new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 200)),
				]);
			} catch { /* release failure is best-effort; the lease will expire server-side */ }
		}
		// Client-side access pre-check: if the viewer lacks video entitlement
		// for this chapter, block the token request and present the paywall
		// via shouldBlockChapter. Reset desired back to mounted so the pump
		// does not re-fire. Every swipe to a locked chapter re-triggers the
		// check so the paywall shows each time.
		if (options.shouldBlockChapter?.(targetChapter)) {
			desiredChapter = mountedChapter;
			retryPending = false;
			return;
		}
		requestedChapter = targetChapter;
		options.onTransitionStart?.(targetChapter);
		try {
			const response = await options.request(targetChapter);
			if (disposed || targetChapter !== desiredChapter) {
				await safeReleaseResponse(response);
				options.onTransitionEnd?.(targetChapter);
				return;
			}
			commitAbortController = new AbortController();
			let result: MediaChapterTransitionCommit;
			try {
				const candidate = options.commit(targetChapter, response, { signal: commitAbortController.signal });
				result = await candidate;
			} catch (error) {
				await safeReleaseResponse(response);
				options.onTransitionEnd?.(targetChapter);
				if (disposed || targetChapter !== desiredChapter || isAbortError(error)) return;
				retryPending = true;
				retryNotBefore = 0;
				options.onError('Video could not load. Tap to retry.', { error, chapter: targetChapter });
				return;
			} finally {
				commitAbortController = null;
			}
			if (disposed || targetChapter !== desiredChapter) {
				await safeReleaseResponse(response);
				options.onTransitionEnd?.(targetChapter);
				return;
			}
			if (!result.committed) {
				await safeReleaseResponse(response);
				options.onTransitionEnd?.(targetChapter);
				retryPending = true;
				retryNotBefore = 0;
				options.onError('Video could not load. Tap to retry.', { error: new Error('media_commit_failed'), chapter: targetChapter });
				return;
			}
			mountedChapter = targetChapter;
			// Track the new mounted lease so the NEXT pump() pre-releases it
			// before requesting the next chapter. The previousLeaseId from the
			// commit is already released by the pre-release in pump() above,
			// so we only need to record the new lease here.
			const responseLeaseId = options.readLeaseId(response);
			if (responseLeaseId) mountedLeaseId = responseLeaseId;
			retryPending = false;
			retryNotBefore = 0;
			options.onTransitionEnd?.(targetChapter);
			// Defensive belt-and-suspenders: also release the previous
			// lease via fire-and-forget in case the pre-release above raced
			// with a fresh swipe. The lease will expire server-side if both
			// fail.
			if (result.previousLeaseId && result.previousLeaseId !== mountedLeaseId) {
				void releaseLease(result.previousLeaseId).then((released) => {
					if (!released && disposed) retryPendingLeaseOnCleanup(result.previousLeaseId!);
				});
			}
		} catch (error) {
			if (disposed || targetChapter !== desiredChapter) {
				options.onTransitionEnd?.(targetChapter);
				return;
			}
			options.onTransitionEnd?.(targetChapter);
			retryPending = true;
			retryNotBefore = retryDeadline(error);
			options.onError(videoTransitionErrorMessage(error), { error, chapter: targetChapter });
		} finally {
			if (requestedChapter === targetChapter) requestedChapter = null;
			if (!disposed && !retryPending && desiredChapter !== mountedChapter) void pump();
		}
	};

	const attemptRetry = (): void => {
		if (disposed || !retryPending || Date.now() < retryNotBefore) return;
		retryPending = false;
		void pump();
	};

	const updateDesired = (chapter: number): void => {
		if (disposed) return;
		const next = clampToKnownTotal(normalizeChapter(chapter));
		if (next === desiredChapter) {
			if (retryPending) attemptRetry();
			return;
		}
		desiredChapter = next;
		retryPending = false;
		commitAbortController?.abort();
		void pump();
	};

	return {
		move(direction) {
			updateDesired(desiredChapter + direction);
		},
		goTo(chapter) {
			updateDesired(chapter);
		},
		syncMounted(chapter) {
			if (disposed || requestedChapter !== null || retryPending || desiredChapter !== mountedChapter) return;
			const next = clampToKnownTotal(normalizeChapter(chapter));
			mountedChapter = next;
			desiredChapter = next;
		},
		retry() {
			attemptRetry();
		},
		cleanup() {
			disposed = true;
			retryPending = false;
			commitAbortController?.abort();
			// The mounted lease is not normally in pendingLeaseIds (it was
			// removed from the set when its release succeeded). Seed it here
			// so a tear-down before the next pump() releases it.
			if (mountedLeaseId) {
				pendingLeaseIds.add(mountedLeaseId);
				mountedLeaseId = undefined;
			}
			for (const leaseId of pendingLeaseIds) retryPendingLeaseOnCleanup(leaseId);
		},
		snapshot() {
			return {
				mountedChapter,
				desiredChapter,
				requestedChapter,
				retryPending,
				retryNotBefore,
				pendingLeaseIds: [...pendingLeaseIds],
				disposed,
			};
		},
	};
}

export function videoTransitionErrorMessage(error: unknown): string {
	if (error instanceof PrototypeApiError) {
		const code = playbackErrorCode(error);
		if (code === 'media_not_found') return 'Video is not available for this chapter.';
		if (code === 'playback_limit_reached') return 'Playback is active on another device. Try again shortly.';
		if (code === 'entitlement_required') return 'Upgrade to unlock video for this chapter.';
		if (code === 'authentication_required') return 'Sign in to watch this chapter.';
	}
	return 'Video could not load. Tap to retry.';
}

export function isImmediateVideoTransitionRetry(error: unknown): boolean {
	if (!(error instanceof PrototypeApiError)) return true;
	const code = playbackErrorCode(error);
	if (code === 'media_not_found' || code === 'playback_limit_reached') return false;
	// Paywall and auth errors should not auto-retry -- the user must upgrade
	// or sign in before another attempt can succeed.
	if (code === 'entitlement_required' || code === 'authentication_required') return false;
	return error.status === 0 || error.status >= 500;
}

function retryDeadline(error: unknown): number {
	if (!(error instanceof PrototypeApiError) || playbackErrorCode(error) !== 'playback_limit_reached') return 0;
	const seconds = typeof error.retryAfterSeconds === 'number' && Number.isFinite(error.retryAfterSeconds)
		? Math.max(0, error.retryAfterSeconds)
		: 0;
	return Date.now() + seconds * 1000;
}

export function playbackErrorCode(error: PrototypeApiError): unknown {
	return error.body && typeof error.body === 'object' && 'error' in error.body
		? (error.body as { error?: unknown }).error
		: undefined;
}

function normalizeChapter(value: number): number {
	return Number.isFinite(value) ? Math.max(1, Math.floor(value)) : 1;
}

function isAbortError(error: unknown): boolean {
	return error instanceof DOMException && error.name === 'AbortError';
}
